import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, User, Tag } from 'lucide-react';
import TiptapDisplay from './TiptapDisplay';
import BlogLayout from './BlogLayout';
import usePublicPosts from '../../hooks/usePublicPosts';
import { BlogPostSkeleton } from '../loading/SkeletonLoader';
import { useLoading } from '../../contexts/LoadingContext';
import { useTransition } from '../../hooks/useTransition';
import {
  formatDate,
  calculateReadingTime,
  getNextPost,
  getPreviousPost,
  generatePostMeta
} from '../../utils/blogUtils';
import { scrollToTop } from '../../utils/scrollUtils';

const BlogPost = ({ isMenuOpen, setIsMenuOpen }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { posts, loading, error, getPostBySlug } = usePublicPosts();
  const { setLoading } = useLoading();
  const { navigateWithTransition } = useTransition();
  const [post, setPost] = useState(null);
  const [nextPost, setNextPost] = useState(null);
  const [previousPost, setPreviousPost] = useState(null);

  // Sync loading state with global loading context
  useEffect(() => {
    setLoading('blogPost', loading);
    return () => setLoading('blogPost', false);
  }, [loading, setLoading]);

  useEffect(() => {
    if (!loading) {
      // If there are no posts at all, navigate back to blog
      if (posts.length === 0) {
        navigate('/blog', { replace: true });
        return;
      }
      
      const foundPost = posts.find(post => post.slug === slug);
      
      if (!foundPost || !foundPost.published) {
        navigate('/blog', { replace: true });
        return;
      }
      
      // Parse tags if they're stored as JSON string
      const processedPost = {
        ...foundPost,
        tags: typeof foundPost.tags === 'string' ? JSON.parse(foundPost.tags) : (foundPost.tags || []),
        publishedAt: foundPost.created_at || foundPost.publishedAt,
        updatedAt: foundPost.updated_at || foundPost.updatedAt
      };
      
      setPost(processedPost);
      
      // Get navigation posts
      const next = getNextPost(processedPost, posts);
      const previous = getPreviousPost(processedPost, posts);
      setNextPost(next);
      setPreviousPost(previous);
      
      // Scroll to top when post loads or changes
      scrollToTop();
    }
  }, [slug, posts, loading, navigate]);

  // Handler for back to blog navigation with smooth transition
  const handleBackToBlog = (e) => {
    e.preventDefault();
    navigateWithTransition('/blog');
  };

  // Handler for previous/next post navigation with smooth transition
  const handlePostNavigation = (postSlug, e) => {
    e.preventDefault();
    navigateWithTransition(`/blog/${postSlug}`);
  };


  if (loading) {
    return (
      <BlogLayout
        title="Loading..."
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
      >
        <BlogPostSkeleton />
      </BlogLayout>
    );
  }

  if (error) {
    return (
      <BlogLayout
        title="Error Loading Post"
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
      >
        <div className="blog-content">
          <div className="empty-state">
            <h2 className="empty-state-title">Error Loading Post</h2>
            <p>There was an error loading the blog post: {error}</p>
            <Link to="/blog" onClick={handleBackToBlog} className="admin-btn mt-4">
              ← Back to Blog
            </Link>
          </div>
        </div>
      </BlogLayout>
    );
  }

  if (!loading && !post) {
    return (
      <BlogLayout
        title="Post Not Found"
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
      >
        <div className="blog-content">
          <div className="empty-state">
            <h2 className="empty-state-title">Post Not Found</h2>
            <p>The blog post you're looking for doesn't exist or has been removed.</p>
            <Link to="/blog" onClick={handleBackToBlog} className="admin-btn mt-4">
              ← Back to Blog
            </Link>
          </div>
        </div>
      </BlogLayout>
    );
  }

  const meta = generatePostMeta(post);

  return (
    <BlogLayout 
      title={post.title}
      description={post.excerpt}
      keywords={post.tags.join(', ')}
      isMenuOpen={isMenuOpen} 
      setIsMenuOpen={setIsMenuOpen}
    >
      <article className="blog-post-container">
        {/* Back Navigation */}
        <div className="mb-6">
          <Link
            to="/blog"
            onClick={handleBackToBlog}
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            Back
          </Link>
        </div>

        {/* Post Header */}
        <header className="blog-post-header">
          <h1 className="blog-post-title">{post.title}</h1>
          
          <div className="blog-post-meta">
            <div className="flex items-center gap-1">
              <Calendar size={16} />
              <span>{formatDate(post.publishedAt)}</span>
            </div>
            <div className="flex items-center gap-1">
              <Clock size={16} />
              <span>{calculateReadingTime(post.content)}</span>
            </div>
          </div>

          <div className="blog-post-tags">
            {post.tags.map((tag, index) => (
              <span key={index} className="blog-tag">
                <Tag size={12} className="inline mr-1" />
                {tag}
              </span>
            ))}
          </div>
        </header>

        {/* Post Content */}
        <div className="blog-content-wrapper">
          <TiptapDisplay content={post.content} />
        </div>

        {/* Post Navigation */}
        {(previousPost || nextPost) && (
          <section className="mt-12">
            <div className="flex justify-between items-center py-8 border-t border-slate-700">
              <div className="flex-1">
                {previousPost && (
                  <Link
                    to={`/blog/${previousPost.slug}`}
                    onClick={(e) => handlePostNavigation(previousPost.slug, e)}
                    className="group flex items-center gap-3 text-slate-400 hover:text-white transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
                      <div>
                        <div className="text-sm text-slate-500">Previous</div>
                        <div className="font-medium">{previousPost.title}</div>
                      </div>
                    </div>
                  </Link>
                )}
              </div>
              
              <div className="flex-1 text-right">
                {nextPost && (
                  <Link
                    to={`/blog/${nextPost.slug}`}
                    onClick={(e) => handlePostNavigation(nextPost.slug, e)}
                    className="group flex items-center justify-end gap-3 text-slate-400 hover:text-white transition-colors"
                  >
                    <div className="text-right">
                      <div className="text-sm text-slate-500">Next</div>
                      <div className="font-medium">{nextPost.title}</div>
                    </div>
                    <ArrowLeft size={20} className="rotate-180 group-hover:translate-x-1 transition-transform" />
                  </Link>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Navigation */}
        <nav className="blog-nav">
          <Link
            to="/blog"
            onClick={handleBackToBlog}
            className="nav-link"
          >
            ← All Posts
          </Link>
          <div className="text-slate-400">
            Share this post
          </div>
        </nav>
      </article>
    </BlogLayout>
  );
};

export default BlogPost;